import { useTranslation } from 'react-i18next';
import { translateBackendError } from '@/api/translateBackendError';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { useCancelWorkspaceClosureMutation } from '@/hooks/useCancelWorkspaceClosureMutation';
import { useWorkspaceClosureQuery } from '@/hooks/useWorkspaceClosureQuery';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { ScheduledClosurePanel } from './ScheduledClosurePanel';
import { UnscheduledClosurePanel } from './UnscheduledClosurePanel';

interface WorkspaceClosureSectionProps {
  onGoToExport: () => void;
}

export function WorkspaceClosureSection({ onGoToExport }: WorkspaceClosureSectionProps) {
  const { t } = useTranslation('workspaceData');
  const closureQuery = useWorkspaceClosureQuery();
  const cancelClosureMutation = useCancelWorkspaceClosureMutation();

  async function cancelClosure() {
    try {
      await cancelClosureMutation.mutateAsync();
      showSuccessNotification(t('closure.scheduled.cancelledToast'));
    } catch (cancelError) {
      showErrorNotification(translateBackendError(t, cancelError));
    }
  }

  function renderBody() {
    if (closureQuery.isLoading === true) {
      return (
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      );
    }
    if (closureQuery.isError === true || closureQuery.data === undefined) {
      return (
        <QueryErrorState
          onRetry={function retry() {
            closureQuery.refetch();
          }}
        />
      );
    }
    if (closureQuery.data.isScheduled === true) {
      return (
        <ScheduledClosurePanel
          closureDueAt={closureQuery.data.closureDueAt}
          isCancelling={cancelClosureMutation.isPending}
          onGoToExport={onGoToExport}
          onCancel={cancelClosure}
        />
      );
    }
    return <UnscheduledClosurePanel />;
  }

  return (
    <Card padding="lg" className="border-zooerrorborder">
      <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-zooerrortext">
        <Icon name="AlertTriangle" />
        {t('closure.dangerZone')}
      </p>
      <h2 className="text-sm font-bold text-zooindigo mt-2">{t('closure.title')}</h2>
      <div className="mt-4">{renderBody()}</div>
    </Card>
  );
}
